import { useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db9krd3k13ra8mig95ag";

const contentScope = [
  {
    icon: "ri-film-line",
    title: "内容范围",
    desc: "以剧情、爱情、悬疑、动作、科幻与治愈类型为主，兼顾不同地区与年份的影片资料整理。",
  },
  {
    icon: "ri-shield-check-line",
    title: "使用说明",
    desc: "站内影片资料与封面为演示素材，播放区域为前端演示占位，不加载也不存储任何视频文件。",
  },
  {
    icon: "ri-copyright-line",
    title: "版权声明",
    desc: "如涉及内容版权问题，可通过下方表单提交说明，我们会尽快核对并处理相关展示信息。",
  },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [charCount, setCharCount] = useState(0);

  useSeo({
    title: "关于我们与联系方式 - 蜜桃成熟时在线观看",
    description:
      "了解蜜桃成熟时在线观看的平台定位、内容范围与使用说明，并通过留言表单反馈使用建议或版权相关问题。",
    keywords: "关于我们,蜜桃成熟时在线观看,联系方式,影视平台介绍",
  });

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      setCharCount(0);
      return;
    }

    formData.delete("contact_alt");
    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMessage || "").toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请检查填写内容后重试。"
            : String(serverMessage || "提交失败，请稍后重试。")
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，留言未能发送，请稍后再试。");
    }
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <p className="mt-5 font-label text-[11px] uppercase tracking-[0.24em] text-primary-600">
            About The Platform
          </p>
          <h1 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            关于蜜桃成熟时在线观看
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
            我们把影片资料、分类筛选和播放演示放在同一个页面体系里，让“找片”这件事尽量简单：打开首页，挑一个类型，两三次点击就能看到影片详情。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.15fr_1fr] lg:items-center">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              一个专注浏览体验的影视内容站
            </h2>
            <div className="mt-4 space-y-3 text-sm leading-7 text-foreground-700">
              <p>
                站点面向 18 岁以上的观影用户，内容以剧情长片为主，覆盖爱情、悬疑、动作、科幻、喜剧与治愈等常见类型。片库按“类型 / 地区 / 年份”三个维度组织，并提供更新时间、热门度与评分三种排序。
              </p>
              <p>
                所有影片资料均为产品演示需要整理的人工数据，包含片名、年份、时长、导演、主演与剧情简介，列表与详情页信息保持一致，方便验证浏览路径是否顺畅。
              </p>
              <p>
                播放区域为前端演示占位：点击后会打开演示播放器界面，并明确标注“播放功能为前端演示，后续接入中”，不会出现播放成功等误导信息。
              </p>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { value: "24", label: "演示影片" },
                { value: "8", label: "影片类型" },
                { value: "5", label: "内容页面" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <p className="font-label text-2xl font-extrabold text-primary-600">{stat.value}</p>
                  <p className="mt-1 text-[11px] text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="aspect-[4/3] w-full">
              <img
                src="https://readdy.ai/api/search-image?query=Stylized%20warm%20editorial%20illustration%20of%20a%20cozy%20small%20cinema%20lounge%20with%20rows%20of%20empty%20peach%20colored%20velvet%20seats%2C%20a%20glowing%20projection%20beam%20cutting%20through%20soft%20dusty%20air%2C%20deep%20charcoal%20walls%20and%20warm%20amber%20accent%20lighting%2C%20painterly%20texture%20with%20subtle%20film%20grain%2C%20no%20text%20no%20letters&width=1200&height=900&seq=peach-about-01&orientation=landscape"
                alt="暖色调影院空间插画"
                title="蜜桃成熟时在线观看 平台介绍"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <p className="px-4 py-3 text-[11px] leading-relaxed text-foreground-500">
              站内使用的封面与插画均为演示素材，用于展示页面版式与信息层级。
            </p>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {contentScope.map((item, index) => (
            <section
              key={item.title}
              className="animate-fade-up rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
              style={{ animationDelay: `${index * 60}ms` }}
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className={`${item.icon} text-lg w-[18px] h-[18px] flex items-center justify-center`} />
              </span>
              <h2 className="mt-4 text-sm font-bold text-foreground-950">{item.title}</h2>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.15fr]">
          <div>
            <p className="font-label text-[11px] uppercase tracking-[0.24em] text-primary-600">
              Contact
            </p>
            <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              联系我们
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              使用建议、影片信息纠错、内容合作或版权相关说明，都可以通过右侧表单提交。我们会在工作日 24 小时内查看，并按提交顺序处理。
            </p>

            <ul className="mt-6 space-y-3">
              {[
                { icon: "ri-mail-send-line", title: "留言表单", desc: "最推荐的方式，可直接提交问题与联系方式。" },
                { icon: "ri-customer-service-2-line", title: "使用反馈", desc: "遇到筛选、搜索异常时，请附上使用的设备与关键词。" },
                { icon: "ri-shield-keyhole-line", title: "版权与内容", desc: "涉及展示信息的问题，请说明具体页面与影片名称。" },
              ].map((item) => (
                <li
                  key={item.title}
                  className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${item.icon} text-base w-4 h-4 flex items-center justify-center`} />
                  </span>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-900">{item.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-5 rounded-lg border border-primary-200 bg-primary-50 p-4">
              <p className="text-xs leading-relaxed text-primary-900">
                本站不提供真实片源，也不接受影片上传请求。请勿在留言中提交任何受版权保护的内容文件。
              </p>
            </div>
          </div>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
            <h2 className="text-lg font-bold text-foreground-950">在线留言</h2>
            <p className="mt-1.5 text-xs text-foreground-500">带 * 的字段为必填项，留言最多 500 字。</p>

            <form data-readdy-form id="contact-form" className="mt-5 space-y-4" onSubmit={onSubmit}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-xs text-foreground-700">
                    称呼 *
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200/70"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-xs text-foreground-700">
                    邮箱 *
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200/70"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-topic" className="mb-1.5 block text-xs text-foreground-700">
                  留言主题 *
                </label>
                <select
                  id="contact-topic"
                  name="topic"
                  required
                  defaultValue=""
                  className="h-10 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200/70"
                >
                  <option value="" disabled>
                    请选择主题
                  </option>
                  <option value="使用建议">使用建议</option>
                  <option value="影片信息纠错">影片信息纠错</option>
                  <option value="内容合作">内容合作</option>
                  <option value="版权相关">版权相关</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-xs text-foreground-700">
                  留言内容 *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  onChange={(event) => setCharCount(event.target.value.length)}
                  placeholder="请简单描述你的问题或建议（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200/70"
                />
                <p className="mt-1 text-right font-label text-[11px] text-foreground-400">
                  {charCount} / 500
                </p>
              </div>

              <div className="field-check-optional">
                <label htmlFor="contact-alt">请勿填写此项</label>
                <input
                  id="contact-alt"
                  name="contact_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className={`${status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} text-base w-4 h-4 flex items-center justify-center`} />
                {status === "submitting" ? "正在发送…" : "提交留言"}
              </button>

              {status === "success" && (
                <div className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3.5 py-3">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-base w-4 h-4 flex items-center justify-center text-primary-600" />
                  <p className="text-xs leading-relaxed text-primary-900">
                    留言已提交，感谢你的反馈。我们会在工作日 24 小时内查看并回复。
                  </p>
                </div>
              )}

              {status === "error" && (
                <div className="flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-3.5 py-3">
                  <i className="ri-error-warning-fill mt-0.5 text-base w-4 h-4 flex items-center justify-center text-accent-700" />
                  <p className="text-xs leading-relaxed text-accent-900">{formError}</p>
                </div>
              )}

              <p className="text-[11px] leading-relaxed text-foreground-400">
                提交即表示你同意我们仅将所填信息用于本次问题回复，不会用于其他用途。
              </p>
            </form>
          </section>
        </div>

        <section className="mt-12 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
          <h2 className="text-lg font-bold text-foreground-950">版权与使用说明</h2>
          <div className="mt-3 space-y-2.5 text-xs leading-6 text-foreground-600">
            <p>
              1. 本站为影视内容浏览演示站点，所有影片名称、简介、评分与封面均为演示数据，不对应真实片源。
            </p>
            <p>
              2. 页面中的播放入口仅展示播放器界面与交互位置，不加载、不缓存、不转存任何视频文件。
            </p>
            <p>
              3. 如权利人认为某条演示信息存在不当展示，可通过上方留言表单提交说明，我们会核对后调整或移除。
            </p>
            <p>4. 建议 18 岁以上用户浏览本站内容，未成年人请在监护人陪同下使用。</p>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}